import { useEffect, useRef } from "react";

/**
 * Refetch the session when the server says this user's permissions moved.
 *
 * WHY THIS EXISTS
 *
 * PermList and CostGatedFields arrive exactly once, on the startup
 * /getuserdata, and nothing refreshed them for the life of the tab. The server
 * re-resolves permissions on every request behind a 30-second cache
 * (node/permissions/resolver.js), so it was only ever the CLIENT that went
 * stale — and it went stale in both directions:
 *
 *   revoked → the buttons stayed on screen and every click 403'd
 *   granted → the feature stayed invisible, and nothing said to reload
 *
 * The second is the one that generates support calls: someone has been told
 * they now have access, and the app disagrees until they happen to reload.
 *
 * The server emits `permissions_changed` after a role edit, a membership
 * change, a per-user override save, or a change to the dealership's gated cost
 * fields (node/helpers/sessions.js notifyPermissionsChanged). The event carries
 * NO permissions — only the fact that they moved — so the server's resolver
 * stays the single authority and this cannot drift from it.
 *
 * Extracted from Cars.jsx rather than left inline because Cars is the root
 * component and nothing renders it in a test, so an effect living there cannot
 * be covered at all.
 *
 * @param socket   the shared socket (null-safe)
 * @param active   only subscribe once signed in
 * @param refetch  called on the event; in the app this is getUserData, which
 *                 re-reads both lists from the one payload they share
 */
export function usePermissionsRefresh(socket, active, refetch) {
  //`refetch` is a fresh closure on every render of the caller, so depending on
  //it directly would tear the listener down and rebuild it on every render.
  //Holding it in a ref keeps one subscription and still always calls the latest
  //version — the alternative, omitting it from the deps, silently pins the
  //first render's closure.
  const latest = useRef(refetch);
  useEffect(() => { latest.current = refetch; }, [refetch]);

  useEffect(() => {
    if (!active || !socket) return undefined;
    const handler = () => {
      if (typeof latest.current === "function") latest.current();
    };
    socket.on("permissions_changed", handler);
    return () => { socket.off("permissions_changed", handler); };
  }, [socket, active]);
}

export default usePermissionsRefresh;
